import { useContext } from 'react';
import { Button, Grid, Typography, FormControlLabel, MenuItem, Checkbox } from '@mui/material';
import { Form, Field, useFormikContext } from 'formik';
import { TextField } from 'formik-mui';
import * as yup from 'yup';
import { Link, useLocation } from 'react-router-dom';
import { channelSchema } from '../ChannelDetailsContext';
import { ChannelType } from 'helpers/enums';
import { applicationContext } from 'helpers/services';

let ChannelForm = () => {
    let router = useLocation();

    let isNew = router.pathname.includes('/addnew');
    let title = isNew ? 'New channel' : `Edit channel`;

    type Channel = yup.InferType<typeof channelSchema>;
    let form = useFormikContext<Channel>();

    let appService = useContext(applicationContext);

    return (
        <Form>
            <Grid container spacing={1} alignItems='center'>
                <Grid item xs>
                    <Typography variant="h4">{title}</Typography>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        component={Link}
                        to={'/admin/channel'}
                    >
                        Cancel
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        type="submit"
                        variant="contained"
                        size="medium"
                        color="primary"
                    >
                        Save
                    </Button>
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems='center' sx={{ mt: 1 }}>
                <Grid item xs>
                    <Field
                        component={TextField}
                        name="channelCode"
                        label="Code"
                        disabled={isNew === false}
                        fullWidth
                        margin="dense"
                        size='small'
                    />
                </Grid>
                <Grid item xs>
                    <Field
                        component={TextField}
                        label="Channel Type"
                        name="channelType"
                        fullWidth
                        disabled={isNew === false}
                        margin="dense"
                        size='small'
                        select
                    >
                        {appService.referenceData.ChannelType.map((channelType) =>
                            <MenuItem key={channelType.key} value={channelType.key}>{channelType.value}</MenuItem>
                        )}
                    </Field>
                </Grid>

                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        name="erpVendorCode"
                        label="Vendor Code"
                        fullWidth
                        margin="dense"
                        size='small'
                        select
                        disabled={isNew === false}
                    >
                        {appService.referenceData.ERPVendorCodes.map((erpVendorCode) =>
                            <MenuItem key={erpVendorCode.key} value={erpVendorCode.key}>{erpVendorCode.value}</MenuItem>
                        )}
                    </Field>
                </Grid>

                <Grid item xs={6}>
                    <Field
                        component={TextField}
                        name="description"
                        label="Description"
                        fullWidth
                        margin="dense"
                        size='small'
                    />
                </Grid>
                <Grid item xs={6}>
                    <Field
                        component={TextField}
                        name="publicName"
                        label="Public name"
                        fullWidth
                        margin="dense"
                        size='small'
                    />
                </Grid>

                <Grid item xs>
                    <FormControlLabel
                        label="All countries"
                        name="allCountries"
                        value={form.values.allCountries}
                        checked={form.values.allCountries}
                        onChange={(e: any) => form.setFieldValue('allCountries', e.target.checked)}
                        control={<Field type="checkbox" name="allCountries" component={Checkbox} />}
                    />
                </Grid>
                {form.values.allCountries === false &&
                    <Grid item xs={6}>
                        <Field
                            component={TextField}
                            label="Country"
                            name="countryCode"
                            fullWidth
                            margin="dense"
                            size='small'
                            select
                        >
                            {appService.referenceData.Country.map((country) =>
                                <MenuItem key={country.key} value={country.key}>{country.value}</MenuItem>
                            )}
                        </Field>
                    </Grid>
                }
                <Grid item xs={12}>
                    <FormControlLabel
                        label="Send fees email"
                        name="sendFeesEmail"
                        value={form.values.sendFeesEmail}
                        checked={form.values.sendFeesEmail}
                        onChange={(e: any) => form.setFieldValue('sendFeesEmail', e.target.checked)}
                        control={<Field type="checkbox" name="sendFeesEmail" component={Checkbox} />}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        label="Is disabled"
                        name="isDisabled"
                        value={form.values.isDisabled}
                        checked={form.values.isDisabled}
                        onChange={(e: any) => form.setFieldValue('isDisabled', e.target.checked)}
                        control={<Field type="checkbox" name="isDisabled" component={Checkbox} />}
                    />
                </Grid>

                {form.values.erpVendorCode === "01A0101" &&
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h6">DUE Details</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                label="Automatic order release"
                                name="automaticOrderRelease"
                                value={form.values.due.automaticOrderRelease}
                                checked={form.values.due.automaticOrderRelease}
                                onChange={(e: any) => form.setFieldValue('due.automaticOrderRelease', e.target.checked)}
                                control={<Field type="checkbox" margin="dense" name="automaticOrderRelease" component={Checkbox} />}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                label="Default customer"
                                name="due.defaultCustomer"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                label="Order reference prefix"
                                name="due.orderIdPrefix"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                name="due.dueOrderType"
                                label="DUE order type"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                name="due.causalCode"
                                label="Causal code"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                name="due.workOrderCode"
                                label="WorkOrder code"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                name="due.customerAddressCode"
                                label="Customer address code"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                    </>
                }

                {form.values.channelType === ChannelType.Lengow &&
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h6">Channel Details</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                label="Account"
                                name="lengow.account"
                                fullWidth
                                margin="dense"
                                size='small'
                                select
                            >
                                {appService.referenceData.Account.map((account) =>
                                    <MenuItem key={account.key} value={account.key}>{account.value}</MenuItem>
                                )}
                            </Field>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                name="lengow.marketplace"
                                label="Marketplace"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Field
                                component={TextField}
                                name="lengow.lengowChannelCode"
                                label="Lengow channel code"
                                fullWidth
                                margin="dense"
                                size='small'
                            />
                        </Grid>
                    </>
                }
            </Grid>
        </Form >
    );
}

export default ChannelForm;